import type { ILearningModuleRepository } from '@/core/domain/learning/module/ILearningModuleRepository.ts'
import {
  LearningModule,
  LearningModuleInput
} from '@/core/domain/learning/module/LearningModule.ts'
import type { ILearningModuleUrlOptions } from '@/core/domain/learning/module/ILearningModuleUrlOptions.ts'
import { LearningModuleUrlOptions } from '@/core/domain/learning/module/LearningModuleUrlOptions.ts'
import { LearningStatus } from '@/core/domain/resources/enums/LearningStatus.ts'
import { apiService } from '@/core/infrastructure/services/apiServiceFactory.ts'
import { NabooError } from '@/core/infrastructure/services/NabooError.ts'

export class LearningModuleRepository implements ILearningModuleRepository {
  /**
   * Récupère un module pour un parcours donné.
   * @param courseId - L'ID du parcours.
   * @param moduleId - L'ID du module.
   * @param options - Les options (statuts, tâche, grains...).
   * @returns Retourne une promesse résolvant un module.
   */
  async fetchLearningModule(
    courseId: number,
    moduleId: number,
    options?: ILearningModuleUrlOptions
  ): Promise<LearningModule> {
    let url = `learning/courses/${courseId}/modules/${moduleId}`

    if (options) {
      const normalizedOptions = LearningModuleUrlOptions.normalizeOptions(options)
      const urlOptions = new LearningModuleUrlOptions(normalizedOptions).getUrlOptions()
      url += urlOptions
    }

    const response = await apiService.get(url)

    return LearningModule.create(response.data)
  }

  /**
   * Récupère tous les modules pour un parcours donné.
   * @param courseId - L'ID du parcours.
   * @param options - Les options (statuts, tâche, grains...).
   * @returns Retourne une promesse résolvant un tableau de modules.
   */
  async fetchAllLearningModules(
    courseId: number,
    options?: ILearningModuleUrlOptions
  ): Promise<LearningModule[]> {
    let url = `learning/courses/${courseId}/modules`

    if (options) {
      const normalizedOptions = LearningModuleUrlOptions.normalizeOptions(options)
      const urlOptions = new LearningModuleUrlOptions(normalizedOptions).getUrlOptions()
      url += urlOptions
    }

    const response = await apiService.get(url)

    if (!Array.isArray(response.data)) {
      throw new NabooError({
        message: "La réponse API n'est pas un tableau",
        status: 500,
        name: 'ApiError',
        code: 'INVALID_RESPONSE_FORMAT'
      })
    }

    return response.data.map((module: any) => LearningModule.create(module))
  }

  /**
   * Crée un module pour un parcours donné.
   * @param courseId - L'ID du parcours.
   * @param module - Les données du module à créer.
   * @returns Retourne une promesse résolvant le module créé.
   */
  async createLearningModule(
    courseId: number,
    module: LearningModuleInput
  ): Promise<LearningModule> {
    const response = await apiService.post(`learning/courses/${courseId}/modules`, module)

    return LearningModule.create(response.data)
  }

  /**
   * Met à jour un module pour un parcours donné.
   * @param courseId - L'ID du parcours.
   * @param moduleId - L'ID du module à mettre à jour.
   * @param module - Les données du module à mettre à jour.
   * @returns Retourne une promesse résolvant le module mis à jour.
   */
  async updateLearningModule(
    courseId: number,
    moduleId: number,
    module: LearningModuleInput
  ): Promise<LearningModule> {
    const response = await apiService.put(
      `learning/courses/${courseId}/modules/${moduleId}`,
      module
    )

    return LearningModule.create(response.data)
  }

  /**
   * Met à jour le statut d'un module pour un parcours donné.
   * @param courseId - L'ID du parcours.
   * @param moduleId - L'ID du module à mettre à jour.
   * @param status - Le nouveau statut du module.
   * @returns Retourne une promesse résolvant le module mis à jour.
   */
  async updateLearningModuleStatus(
    courseId: number,
    moduleId: number,
    status: LearningStatus.Values
  ): Promise<LearningModule> {
    const response = await apiService.put(`learning/courses/${courseId}/modules/${moduleId}/status`, {
      status
    })

    return LearningModule.create(response.data)
  }
}
